<script setup lang="ts">
    import { alignItems, borderWidth, cascade, flexValues, layoutBindings, mapBreakpoints, margin, single, size, space } from './layout';
    import type { BoxProps, LayoutRadius } from './types';

    const props = withDefaults(defineProps<BoxProps>(), {
        as: 'div',
        p: undefined,
        px: undefined,
        py: undefined,
        pt: undefined,
        pr: undefined,
        pb: undefined,
        pl: undefined,
        m: undefined,
        mx: undefined,
        my: undefined,
        mt: undefined,
        mr: undefined,
        mb: undefined,
        ml: undefined,
        w: undefined,
        h: undefined,
        miw: undefined,
        maw: undefined,
        mih: undefined,
        mah: undefined,
        withBorder: false,
        borderColor: undefined,
        borderSide: undefined,
        borderStyle: undefined,
        radius: undefined,
        flex: undefined,
        alignSelf: undefined,
    });

    defineSlots<{
        default?(): unknown;
    }>();

    const radii: Record<LayoutRadius, string> = {
        none: '0px',
        sm: 'var(--radius-sm)',
        md: 'var(--radius-md)',
        lg: 'var(--radius-lg)',
        xl: 'var(--radius-xl)',
        full: '9999px',
    };

    const hasBorder = computed(() => props.withBorder || props.borderColor !== undefined || props.borderSide !== undefined || props.borderStyle !== undefined);

    // Width, style and color are always set together, behind the one `data-border` flag.
    const border = computed(() => ({
        'border-width': hasBorder.value ? { base: borderWidth(props.borderSide) } : {},
        'border-style': hasBorder.value ? { base: props.borderStyle ?? 'solid' } : {},
        'border-color': hasBorder.value ? { base: `var(--color-border-${props.borderColor ?? 'secondary'})` } : {},
    }));

    const bindings = computed(() => ({
        ...layoutBindings({
            pt: mapBreakpoints(cascade(props.pt, props.py, props.p), space),
            pr: mapBreakpoints(cascade(props.pr, props.px, props.p), space),
            pb: mapBreakpoints(cascade(props.pb, props.py, props.p), space),
            pl: mapBreakpoints(cascade(props.pl, props.px, props.p), space),
            mt: mapBreakpoints(cascade(props.mt, props.my, props.m), margin),
            mr: mapBreakpoints(cascade(props.mr, props.mx, props.m), margin),
            mb: mapBreakpoints(cascade(props.mb, props.my, props.m), margin),
            ml: mapBreakpoints(cascade(props.ml, props.mx, props.m), margin),
            w: single(props.w, size),
            h: single(props.h, size),
            miw: single(props.miw, size),
            maw: single(props.maw, size),
            mih: single(props.mih, size),
            mah: single(props.mah, size),
            ...border.value,
            radius: single(props.radius, (value) => radii[value]),
            flex: single(props.flex, (value) => flexValues[value]),
            'align-self': alignItems(props.alignSelf),
        }),
        'data-border': hasBorder.value ? '' : undefined,
        'data-flex-fill': props.flex === 'fill' ? '' : undefined,
    }));
</script>

<template>
    <component
        :is="as"
        data-slot="box"
        class="ui-box"
        v-bind="bindings"
    >
        <slot />
    </component>
</template>

<style scoped src="./tokens.css"></style>

<style scoped src="./box.css"></style>
